import { koComponent } from '../../../ko-utils/index';
import * as siteConfig from '../../../site-config/config';
import createUuid from '../../../utils/uuid';
import tryMakeUrlDynamic from '../../../utils/tryMakeUrlDynamic';
import CardEditorBase from '../../../common/card-editor/CardEditorBase';
import { isFlagOn } from '../../../utils/flags';

class HelpEditor extends CardEditorBase {
    constructor(params) {
        super('help');

        this.parent = params.parent;
        this.help = ko.unwrap(params.help);

        this.triggers = [
            { name: 'On target element hover', type: 'onhover' },
            { name: 'Always', type: 'onload' },
            { name: 'Until clicked (hotspot)', type: 'onuntilclicked' },
        ];

        this.cardTriggers = [
            { name: 'On hint clicked', type: undefined },
            { name: 'On hint hovered', type: 'hover' },
        ]

        this.iconTypes = [
            { name: 'Question mark', type: 'question_mark' },
            { name: 'Info icon', type: 'info' },
            { name: 'Beacon (hotspot)', type: 'beacon' },
            { name: '"1" icon', type: 'digit-1' },
            { name: '"2" icon', type: 'digit-2' },
            { name: '"3" icon', type: 'digit-3' },
            { name: '"4" icon', type: 'digit-4' },
            { name: '"5" icon', type: 'digit-5' },
            { name: '"6" icon', type: 'digit-6' },
            { name: '"7" icon', type: 'digit-7' },
            { name: '"8" icon', type: 'digit-8' },
            { name: '"9" icon', type: 'digit-9' },
        ];

        this.selectedTrigger = ko.computed({
            read: () => {
                var trigger = ko.unwrap(this.help.trigger);

                if (!trigger) {
                    return this.triggers[0].type;
                } else {
                    return ko.unwrap(trigger.type);
                }
            },
            write: val => {
                this.help.trigger(this.triggers.filter(i => i.type == val)[0]);

                this.autosave();
            }
        });

        this.selectedCardTrigger = ko.computed({
            read: () => {
                var trigger = ko.unwrap(this.help.cardTrigger);

                if (!trigger) {
                    return this.cardTriggers[0].type;
                } else {
                    return ko.unwrap(trigger.type);
                }
            },
            write: val => {
                this.help.cardTrigger(this.cardTriggers.filter(i => i.type == val)[0]);

                this.autosave();
            }
        });

        this.isSelected = ko.observable(params.isSelected || false);
        this.isSelected.subscribe(val => {
            if (val) {
                params.parent.setSelectedHelp(this);
            } else {
                params.parent.setSelectedHelp(null);
            }
        });

        let isNew = false;

        if (!this.help.id || !this.help.id()) {
            this.help.id(createUuid());

            isNew = true;
        }

        if (!this.help.backdrop()) {
            this.help.backdrop(new siteConfig.BackdropDescriptor());
        }

        this.help.backdrop().type.subscribe(() => {
            this.changeCardProps();
        });

        if (!this.help.element()) {
            this.help.element(new siteConfig.ElementDescriptor());
        }

        const defaultizeIconPosition = (val) => {
            if (!this.help.iconPosition()) {
                this.help.iconPosition(new siteConfig.PositionValue());
            }
            if (val == 'beacon') {
                this.help.iconPosition().position('r');
            } else {
                this.help.iconPosition().position('or');
            }

            this.changeCardProps();
        };

        this.help.iconType.subscribe(defaultizeIconPosition);
        if (!this.help.iconPosition() || !this.help.iconPosition().position()) {
            defaultizeIconPosition(this.help.iconType());
        }

        if (!this.help.element() || !this.help.element().query) {
            this.help.element(new siteConfig.ElementDescriptor());
        }

        this.help.element().query.subscribe(() => {
            this.showCardInWeb();
            this.autosave();
        });

        if (this.help.targetUrl()) {
            this.initAutosaveHandlers(this.help.targetUrl(), ['parametrized'])
        }

        document.addEventListener('cardElementQueried', (data) => {
            if (data.detail.cardId === this.help.id()) {
                if (!this.help.element()) {
                    this.help.element(new siteConfig.ElementDescriptor());
                }

                if (isFlagOn('iframes')) {
                    if (!ko.isObservable(this.help.element().iframe) && data.detail.iframe) {
                        this.help.element().iframe = ko.observable();
                    }

                    this.help.element().iframe(data.detail.iframe);
                }

                this.help.element().query(data.detail.query);

                var sv = new siteConfig.StringValue();
                sv.parametrized(tryMakeUrlDynamic(data.detail.url, this.parent.editorSettings.dontDetectDynamicUrlParts()));

                this.help.targetUrl(sv);
                this.initAutosaveHandlers(this.help.targetUrl(), ['parametrized'])
            }
        });
        this.listenToThisId('cardSelectorChanged', this.help.id(), data => {
            if (isFlagOn('iframes')) {
                if (!ko.isObservable(this.help.element().iframe) && data.detail.iframe) {
                    this.help.element().iframe = ko.observable();
                }

                this.help.element().iframe(data.detail.iframe);
            }

            this.help.element().query(data.detail.query);

            this.autosave();
        });
        document.addEventListener('cardContentSet', (data) => {
            if (data.detail.cardId === this.help.id()) {
                //this.isSelected(false);
                this.help.content(new siteConfig.CardContentDescriptor(data.detail.content));
            }
        });

        document.addEventListener('deselectCard', data => {
            if (data.detail.cardId === this.help.id()) {
                this.isSelected(false);
            }
        });

        document.addEventListener('relocateCard', data => {
            if (data.detail.cardId === this.help.id()) {
                this.locateElement();
            }
        });

        this.listenToThisIdOnce('closeCard', this.help.id(), data => {
            window.parent.postMessage({ action: 'close-card', helpId: this.help.id() }, '*');
        });

        this.listenToThisId('cardResumeEditing', this.help.id(), data => {
            this.showCardInWeb();
        });

        this.hasElementsSelected = ko.pureComputed(() => {
            if (this.help.element()) {
                return ko.unwrap(this.help.element().query) != null || ko.unwrap(this.help.element().id) != null;
            } else {
                return false;
            }
        });

        if (isNew) {
            this.locateElement();
        }

        this.headerVisible = ko.observable(this.getVisibilityPropSafe('title'));
        this.headerVisible.subscribe(() => {
            this.headerFooterVisibilityChanged();
        });
        this.footerVisible = ko.observable(this.getVisibilityPropSafe('footer'));
        this.footerVisible.subscribe(() => {
            this.headerFooterVisibilityChanged();
        });
        this.dismissButtonVisible = ko.observable(this.getFooterButtonVisibilityPropSafe('dismissButton', false));
        this.dismissButtonVisible.subscribe(() => {
            this.headerFooterVisibilityChanged();
        });
        this.closeButtonVisible = ko.observable(this.getFooterButtonVisibilityPropSafe('closeButton'));
        this.closeButtonVisible.subscribe(() => {
            this.headerFooterVisibilityChanged();
        });

        this.initAutosaveHandlers(this.help, ['showDelayMs']);
    }

    get currentSelectorObservable() {
        return this.help.element().query;
    }

    findParentElement() {
        window.parent.postMessage({
            action: 'select-parent-element',
            helpId: ko.unwrap(this.help.id),
            selector: ko.unwrap(this.help.element().query),
            selectorSettings: this.parent.editorSettings.toJS()
        }, '*');
    }

    initAutosaveHandlers(baseObject, props) {
        const subscribeAndNotify = (prop) => {
            if (baseObject && baseObject[prop] && baseObject[prop].subscribe) {
                baseObject[prop].subscribe((val) => {
                    this.autosave();
                });
            }
        };

        Array.from(props).forEach(p => subscribeAndNotify(p));
        //subscribeAndNotify('blockedKeys');
        //subscribeAndNotify('showDelayMs');
    }

    getFooterButtonVisibilityPropSafe(btnName, defIfNotExists = true) {
        const cont = ko.unwrap(this.help.content);
        const footer = ko.unwrap(cont.footer);
        if (!footer) return defIfNotExists;

        if (defIfNotExists) {
            return ko.unwrap(footer[btnName].visible) !== false;
        } else {
            return ko.unwrap(footer[btnName].visible) === true;
        }
    }

    showHelp(id) {
        window.parent.postMessage({
            action: 'pf_show_help',
            id
        }, '*');
    }

    getVisibilityPropSafe(prop) {
        const cont = ko.unwrap(this.help.content);
        if (!cont) return false;
        const p = ko.unwrap(cont[prop]);
        if (!p) return false;

        return ko.unwrap(p.visible);
    }

    headerFooterVisibilityChanged() {
        window.parent.postMessage({
            action: 'changeHeaderFooter',
            headerVisibility: this.headerVisible(),
            footerVisibility: this.footerVisible(),
            closeButtonVisibility: this.closeButtonVisible(),
            dismissButtonVisibility: this.dismissButtonVisible(),
        }, '*');
        this.autosave();
    }

    positionPropChanged(propName) {
        this.changeCardProps();
    }

    locateElement() {
        this.isSelected(true);
        window.parent.postMessage({ action: 'locateElement', helpId: this.help.id(), helpJson: JSON.parse(JSON.stringify(this.help.toJS())), selectorSettings: this.parent.editorSettings.toJS(), language: this.parent.selectedLanguage() }, '*');
    }

    showCardInWeb() {
        this.isSelected(true);
        window.parent.postMessage({ action: 'showCard', helpId: this.help.id(), helpJson: JSON.parse(JSON.stringify(this.help.toJS())), language: this.parent.selectedLanguage() }, '*');
    }

    changeCardProps() {
        window.parent.postMessage({ action: 'changeProps', helpId: this.help.id(), helpJson: JSON.parse(JSON.stringify(this.help.toJS())), language: this.parent.selectedLanguage() }, '*');
        this.autosave();
    }

    autosave() {
        this.parent.autosave();
    }
}

export default koComponent('help-editor', HelpEditor);